import { findScreens } from "../../server/rules/shell";
import { el } from "../ui/dom";
import { openDialog } from "../ui/modal";
import { icon } from "../ui/icons";
import type { Me } from "./types";

// Ctrl K (and Cmd K): search over the screens this person can open (mockup .cmdk). Arrows move,
// Enter goes, Esc closes. The list comes from /api/me's menus, so a screen someone can't open is
// never found.

export interface Found {
  path: string;
  title: string;
  section: string;
  icon: string;
}

/** Home, then every screen in the person's menus. */
export function screensOf(me: Me): Found[] {
  return [
    { path: "/", title: "Home", section: "", icon: "home" },
    ...me.menus.flatMap((m) =>
      m.screens.map((x) => ({
        path: `/${m.id}/${x.id}`,
        title: x.title,
        section: m.name,
        icon: x.icon,
      })),
    ),
  ];
}

let open = false;

export function openSearch(me: Me, go: (path: string) => void) {
  if (open) return;
  open = true;
  const all = screensOf(me);
  const input = el("input", {
    type: "text",
    id: "fq",
    placeholder: "Search screens…",
    autocomplete: "off",
    "aria-label": "Search screens",
    role: "combobox",
    "aria-controls": "flist",
    "aria-expanded": "true",
  });
  const list = el("div", { id: "flist", class: "flist", role: "listbox", "aria-label": "Screens" });
  let shown: Found[] = [];
  let at = 0;

  const draw = () => {
    shown = findScreens(all, input.value).slice(0, 12);
    at = Math.min(at, Math.max(0, shown.length - 1));
    if (shown.length === 0) {
      list.replaceChildren(el("p", { class: "muted small" }, "No screen by that name."));
      return;
    }
    list.replaceChildren(
      ...shown.map((f, i) => {
        const b = el(
          "button",
          {
            type: "button",
            class: `fitem${i === at ? " first" : ""}`,
            role: "option",
            "aria-selected": i === at ? "true" : "false",
            "data-path": f.path,
          },
          icon(f.icon),
          el(
            "span",
            {},
            el("b", {}, f.title),
            f.section ? el("span", { class: "muted small" }, ` · ${f.section}`) : "",
          ),
          i === at ? el("span", { class: "kbd" }, "Enter") : "",
        );
        b.addEventListener("click", () => pick(f));
        b.addEventListener("mousemove", () => {
          if (at !== i) {
            at = i;
            draw();
          }
        });
        return b;
      }),
    );
    list.querySelector(".fitem.first")?.scrollIntoView({ block: "nearest" });
  };

  const close = openDialog(
    el(
      "div",
      {},
      el("div", { class: "cmdk-in" }, icon("search"), input, el("span", { class: "kbd" }, "Esc")),
      list,
    ),
    { label: "Search", className: "cmdk", top: true, onClose: () => (open = false) },
  );
  const pick = (f: Found) => {
    close();
    go(f.path);
  };

  input.addEventListener("input", () => {
    at = 0;
    draw();
  });
  input.addEventListener("keydown", (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (shown.length) at = (at + (e.key === "ArrowDown" ? 1 : shown.length - 1)) % shown.length;
      draw();
    } else if (e.key === "Enter") {
      e.preventDefault();
      const f = shown[at];
      if (f) pick(f);
    }
  });
  draw();
  input.focus();
}
